import './cartpage.css';
import { useState, useEffect } from 'react';
import { ShoppingCart, Trash } from 'lucide-react';
import { useNavigate } from "react-router-dom";

function Cart() {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [message, setMessage] = useState(null); 
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();

  // Load config.json
  useEffect(() => {
    fetch("/config.json")
      .then(res => res.json())
      .then(data => setConfig(data))
      .catch(err => console.error("Failed to load config.json", err));
  }, []);

  // Auto-hide messages
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Check session
  useEffect(() => {
    if (!config) return;

    const checkSession = async () => {
      try {
        const res = await fetch(`${config.BASE_URL}${config.CHECK_SESSION}`, {
          method: "POST",
          credentials: "include",
        });
        const data = await res.json();
        if (data.active) {
          setIsLoggedIn(true);
        } else {
          setIsLoggedIn(false);
          setError("NOT_LOGGED_IN");
        }
      } catch {
        setIsLoggedIn(false);
        setError("NOT_LOGGED_IN");
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, [config]);

  // Fetch cart items only if logged in
  useEffect(() => {
    if (!config || !isLoggedIn) return;

    const fetchCartItems = async () => {
      try {
        const response = await fetch(`${config.BASE_URL}${config.GET_CART}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        if (!response.ok) throw new Error("Network response was not ok");

        const data = await response.json();
        if (data.success) {
          setCartItems(data.cart);
        } else {
          setError(data.message || "Failed to fetch cart");
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCartItems();
  }, [config, isLoggedIn]);

  // Update quantity in backend
  const updateCartQuantity = async (productId, newQuantity) => {
    if (!config) return;
    try {
      const response = await fetch(`${config.BASE_URL}${config.UPDATE_CART}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ product_id: productId, quantity: newQuantity })
      });
      const data = await response.json();
      if (!data.success) {
        setMessage({ type: "error", text: data.message || "Failed to update quantity" });
      } else {
        setMessage({ type: "success", text: "Quantity updated successfully!" });
      }
    } catch (err) {
      console.error("Quantity update failed:", err);
      setMessage({ type: "error", text: "Something went wrong while updating." });
    }
  };

  const goToProductDetails = async (productId) => {
  if (!config) return;

  try {
    const res = await fetch(`${config.BASE_URL}${config.GET_PRODUCTS}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include"
    });

    const data = await res.json();
    if (data.success && data.products && data.products.length > 0) {
      // Find the product with the matching product_id
      const product = data.products.find(p => p.product_id === productId);
      if (product) {
        navigate("/product-details", { state: { product } });
      } else {
        setMessage("Product not found.");
      }
    } else {
      setMessage("Failed to load products.");
    }
  } catch (err) {
    console.error("Error fetching product details:", err);
    setMessage("Error loading product details.");
  }
};



  // Delete item from cart
  const deleteCartItem = async (productId) => {
    if (!config) return;

    try {
      const response = await fetch(`${config.BASE_URL}${config.DELETE_CART}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ product_id: productId })
      });

      const data = await response.json();
      if (data.success) {
        const updatedCart = cartItems.filter(item => item.product_id !== productId);
        setCartItems(updatedCart);

        const newCount = updatedCart.length;
        window.cartCount = newCount;
        localStorage.setItem("cartCount", newCount);
        window.dispatchEvent(new CustomEvent("cartUpdated", { detail: newCount }));

        setMessage({ type: "success", text: "Item removed from cart." });
      } else {
        setMessage({ type: "error", text: data.message || "Failed to delete item" });
      }
    } catch (err) {
      console.error("Delete item failed:", err);
      setMessage({ type: "error", text: "Error while deleting item" });
    }
  };

  const handleRemoveItem = (id) => deleteCartItem(id);

  // Calculate subtotal, tax, total
 const subtotal = cartItems.reduce(
  (total, item) => total + (parseFloat(item.price) + parseFloat(item.tax)) * item.quantity,
  0
);
  const taxRate = 0.18;
  const totalTax = subtotal * taxRate;
  const total = subtotal + totalTax;

  if (loading) return <p>Loading...</p>;
  if (error === "NOT_LOGGED_IN") {
    return (
      <div className="cart-page not-logged-in">
        <h2>Please log in to view your cart.</h2>
        <p>"Our robots don’t serve strangers… become a member, human!"</p>
        <button className="login-button" onClick={() => navigate("/login")}>
          Login
        </button>
      </div>
    );
  }
  if (error) return <p>Error: {error}</p>;

  return (
    <div className="cart-page">
      <h1 className="cart-heading">
        <ShoppingCart size={24} className="cart-icon" /> Your Cart
      </h1>

      {cartItems.length === 0 ? (
        <p className="empty-cart-message">Your cart is currently empty.</p>
      ) : (
        <>
          <div className="cart-items">
            {cartItems.map(item => {
              const imagePath = `/product_thumbnails/${item.product_id}/${item.image_path}`;
              return (
                <div key={item.product_id} className="cart-item">
                  <div className="cart-item-image-wrapper"    onClick={() => goToProductDetails(item.product_id)}
  style={{ cursor: "pointer" }}>  
                    <img src={imagePath} alt={item.title} className="cart-item-image" />
                    <div className="quantity-badge">{item.quantity}</div>
                  </div>

                  <div className="cart-item-details">
                    <h2 className="cart-item-title">{item.title}</h2>
                    <p className="cart-item-price"> ₹{(parseFloat(item.price) + parseFloat(item.tax)).toFixed(2)}</p>

                    <div className="quantity-buttons">
                      <button 
                        className="minus-button"
                        onClick={() => {
                          const newQty = Math.max(1, item.quantity - 1);
                          setCartItems(cartItems.map(ci => ci.product_id === item.product_id ? { ...ci, quantity: newQty } : ci));
                          updateCartQuantity(item.product_id, newQty);
                        }}
                      >-</button>

                      <button 
                        className="plus-button"
                        onClick={() => {
                          const newQty = item.quantity + 1;
                          setCartItems(cartItems.map(ci => ci.product_id === item.product_id ? { ...ci, quantity: newQty } : ci));
                          updateCartQuantity(item.product_id, newQty);
                        }}
                      >+</button>
                    </div>
                  </div>

                  <button className="remove-item-button" onClick={() => handleRemoveItem(item.product_id)}>
                    <Trash size={18} />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="cart-summary">
            <h2>Cart Summary</h2>
            <div className="cart-totals">
              <p>Subtotal: ₹{subtotal.toFixed(2)}</p>
              <p>Tax (18%) : ₹{totalTax.toFixed(2)}</p>
              <p><strong>Total: ₹{total.toFixed(2)}</strong></p>
            </div>

            <div className="cart-actions">
              <button className="checkout-button" onClick={() => navigate("/checkout")}>Proceed to Checkout</button>
              <button className="go-shop-button" onClick={() => navigate("/products")}>Go to Shop</button>
            </div>
          </div>
        </>
      )}

      {message && (
        <div 
          style={{
            position: "fixed",
            top: "50px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(10px)",
            padding: "12px 24px",
            borderRadius: "12px",
            color: message.type === "success" ? "#0f5132" : "#842029",
            border: `1px solid ${message.type === "success" ? "#badbcc" : "#f5c2c7"}`,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            fontSize: "16px",
            fontWeight: "500",
            zIndex: 1000,
          }}
        >
          {message.text}
        </div>
      )}
    </div>
  );
}

export default Cart;
